import { useEffect, useMemo, useState } from 'react';
import { useApp } from '../context';
import type { PlexItem } from '../plex';
import { Card } from '../components/Card';
import { linkProps } from '../router';

/**
 * "Jouw jaar": wat dit profiel heeft gekeken, uit Plex zelf (laatst bekeken per titel).
 * Telt elke film en aflevering één keer per periode.
 */

type Period = 'month' | 'year' | 'last12' | 'all';
const PERIODS: [Period, string][] = [['month', 'Deze maand'], ['year', 'Dit jaar'], ['last12', 'Afgelopen 12 maanden'], ['all', 'Altijd']];

function since(p: Period): number {
  const d = new Date();
  if (p === 'month') return new Date(d.getFullYear(), d.getMonth(), 1).getTime() / 1000;
  if (p === 'year') return new Date(d.getFullYear(), 0, 1).getTime() / 1000;
  if (p === 'last12') return (Date.now() - 365 * 86_400_000) / 1000;
  return 0;
}

const DAYS = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];
const PARTS: [number, number, string][] = [[0, 6, 'nacht'], [6, 12, 'ochtend'], [12, 18, 'middag'], [18, 24, 'avond']];

export function LegacyStats() {
  const { plex, libraries, me } = useApp();
  const [period, setPeriod] = useState<Period>('year');
  const [movies, setMovies] = useState<PlexItem[] | null>(null);
  const [episodes, setEpisodes] = useState<PlexItem[] | null>(null);

  // Alles wat ooit bekeken is, nieuwste eerst; stoppen zodra we buiten de periode (of bij nooit bekeken) komen
  useEffect(() => {
    let live = true;
    setMovies(null);
    setEpisodes(null);
    const from = since(period);
    const collect = async (type: 1 | 4) => {
      const out: PlexItem[] = [];
      for (const lib of libraries.filter((l) => l.type === (type === 1 ? 'movie' : 'show'))) {
        for (let start = 0; start < 5000; start += 200) {
          const r = await plex.libraryItems(lib.key, { sort: 'lastViewedAt:desc', type, start, size: 200 }).catch(() => null);
          if (!r) break;
          let done = r.items.length < 200;
          for (const it of r.items) {
            if (!it.lastViewedAt || it.lastViewedAt < from) {
              done = true;
              break;
            }
            out.push(it);
          }
          if (done) break;
        }
      }
      return out;
    };
    void collect(1).then((m) => live && setMovies(m));
    void collect(4).then((e) => live && setEpisodes(e));
    return () => {
      live = false;
    };
  }, [plex, libraries, period]);

  const stats = useMemo(() => {
    if (!movies || !episodes) return null;
    const minutes = [...movies, ...episodes].reduce((n, i) => n + (i.duration ?? 0) / 60_000, 0);
    const shows = new Map<string, { title: string; count: number; key?: string; thumb?: string }>();
    for (const e of episodes) {
      const k = e.grandparentRatingKey ?? e.grandparentTitle ?? '?';
      const cur = shows.get(k) ?? { title: e.grandparentTitle ?? '?', count: 0, key: e.grandparentRatingKey, thumb: e.grandparentThumb };
      cur.count += 1;
      shows.set(k, cur);
    }
    const genres = new Map<string, number>();
    for (const m of movies) for (const g of m.Genre ?? []) genres.set(g.tag, (genres.get(g.tag) ?? 0) + 1);
    const when = new Map<string, number>();
    for (const i of [...movies, ...episodes]) {
      const d = new Date((i.lastViewedAt ?? 0) * 1000);
      const part = PARTS.find(([a, b]) => d.getHours() >= a && d.getHours() < b)?.[2] ?? '';
      const k = `${DAYS[d.getDay()]}${part}`;
      when.set(k, (when.get(k) ?? 0) + 1);
    }
    const favMoment = [...when.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
    return {
      hours: Math.round(minutes / 60),
      movies: movies.length,
      episodes: episodes.length,
      showCount: shows.size,
      topShows: [...shows.values()].sort((a, b) => b.count - a.count).slice(0, 6),
      topGenres: [...genres.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6),
      favMoment,
    };
  }, [movies, episodes]);

  const maxGenre = stats?.topGenres[0]?.[1] ?? 1;

  return (
    <main className="page stats-page">
      <header className="page-head">
        <h1>{period === 'year' ? `Jouw ${new Date().getFullYear()}` : 'Jouw kijkoverzicht'}</h1>
        <span className="subtle">{me.profile?.title}</span>
      </header>
      <div className="filterbar" role="tablist" aria-label="Periode" data-focus-group>
        {PERIODS.map(([id, label]) => (
          <button key={id} role="tab" className="chip" aria-selected={period === id} aria-pressed={period === id} onClick={() => setPeriod(id)}>{label}</button>
        ))}
      </div>

      {!stats && <p className="placeholder-row" aria-busy="true">Je kijkgeschiedenis doorrekenen…</p>}
      {stats && stats.movies + stats.episodes === 0 && <p className="placeholder-row">In deze periode nog niets gekeken. Tijd voor de bank!</p>}
      {stats && stats.movies + stats.episodes > 0 && (
        <>
          <div className="stats-tiles">
            <div className="stats-tile"><strong>{stats.hours.toLocaleString('nl-NL')}</strong><span>uur gekeken</span></div>
            <div className="stats-tile"><strong>{stats.movies}</strong><span>{stats.movies === 1 ? 'film' : 'films'}</span></div>
            <div className="stats-tile"><strong>{stats.episodes}</strong><span>afleveringen</span></div>
            <div className="stats-tile"><strong>{stats.showCount}</strong><span>{stats.showCount === 1 ? 'serie' : 'series'}</span></div>
          </div>
          {stats.favMoment && <p className="stats-fact">Je kijkt het vaakst op <strong>{stats.favMoment}</strong>.</p>}

          <div className="stats-cols">
            {stats.topShows.length > 0 && (
              <section className="stats-block" aria-label="Meest gekeken series">
                <h2>Meest gekeken series</h2>
                <ol className="stats-shows">
                  {stats.topShows.map((s, i) => {
                    const img = plex.image(s.thumb, 120, 180);
                    const body = (
                      <>
                        <span className="stats-rank">{i + 1}</span>
                        {img && <img src={img} alt="" />}
                        <span className="stats-show-title">{s.title}</span>
                        <span className="subtle">{s.count} afl.</span>
                      </>
                    );
                    return <li key={s.title}>{s.key ? <a {...linkProps(`/item/${s.key}`)}>{body}</a> : <span>{body}</span>}</li>;
                  })}
                </ol>
              </section>
            )}
            {stats.topGenres.length > 0 && (
              <section className="stats-block" aria-label="Favoriete genres">
                <h2>Favoriete genres (films)</h2>
                <ul className="stats-genres">
                  {stats.topGenres.map(([g, n]) => (
                    <li key={g}>
                      <span>{g}</span>
                      <span className="stats-bar" aria-hidden="true"><span style={{ width: `${Math.round((n / maxGenre) * 100)}%` }} /></span>
                      <span className="subtle">{n}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>

          {movies && movies.length > 0 && (
            <section className="row" aria-label="Laatst gekeken films">
              <h2>Laatst gekeken films</h2>
              <div className="row-track" data-focus-group>
                {movies.slice(0, 20).map((m) => <Card key={m.ratingKey} item={m} kind="poster" />)}
              </div>
            </section>
          )}
        </>
      )}
      <div className="footer-space" />
    </main>
  );
}
